import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getDecisions } from '../../lib/supabase';
import { authLogger } from '../../lib/auth/authLogger';
import { Decision } from '../../types/database';
import { PlusCircle, Loader, AlertTriangle, RefreshCw } from 'lucide-react';

export default function DecisionList() {
  const { user } = useAuth();
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [retryTimeout, setRetryTimeout] = useState<number | null>(null);

  // Cleanup function for retry timeout
  useEffect(() => {
    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [retryTimeout]);

  const fetchDecisions = useCallback(async (): Promise<void> => {
    if (!user) {
      authLogger.debug('DECISIONS', 'No user found');
      setError('You must be signed in to view your decisions');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await getDecisions(user.id); 
      
      if (error) {
        // Handle session errors specifically
        if (error.message.includes('session') || error.message.includes('JWT')) {
          authLogger.error('ERROR', 'Session error', error);
          setError('Your session has expired. Please sign in again.');
          return;
        }
        throw error;
      }
      
      setDecisions(data ?? []);
      if (retryCount > 0) {
        setRetryCount(0); // Only reset if we had retries
      }
    } catch (err) {
      console.error('Failed to fetch decisions:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load decisions';
      
      // Handle specific error cases
      let finalError = errorMessage;
      if (errorMessage.includes('Database setup incomplete')) {
        finalError = 'The system is currently being initialized. Please try again in a moment.';
      } else if (errorMessage.includes('network')) {
        finalError = 'Unable to connect to the server. Please check your internet connection.';
      } else if (errorMessage.includes('session')) {
        finalError = 'Your session has expired. Please sign in again.';
      }
      
      setError(finalError);
      
      // Auto-retry up to 3 times with exponential backoff
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        const timeoutId = window.setTimeout((): void => {
          setRetryCount(prev => prev + 1);
          // Retry on network errors or database setup issues
          if (err instanceof Error && (
            err.message.includes('network') ||
            err.message.includes('Database setup incomplete')
          )) {
            fetchDecisions();
          }
        }, delay);
        setRetryTimeout(timeoutId);
      }
    } finally {
      setLoading(false);
    }
  }, [user, retryCount]);

  useEffect(() => {
    fetchDecisions();
    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [fetchDecisions, retryTimeout]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
        <Loader className="h-8 w-8 text-indigo-500 animate-spin" />
        <p className="text-gray-600">Loading your decisions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex flex-col items-center gap-4">
          <AlertTriangle className="h-8 w-8 text-red-400" />
          <div className="text-center">
            <h3 className="text-lg font-medium text-red-800 mb-2">Error loading decisions</h3>
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <button
              onClick={() => {
                setRetryCount(0);
                fetchDecisions();
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Your Decisions</h2>
        <Link
          to="/decisions/new"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          New Decision
        </Link>
      </div>

      {decisions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No decisions yet</h3>
          <p className="text-gray-600 mb-4">Start by creating your first decision</p>
          <Link
            to="/decisions/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Create Decision
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {decisions.map((decision) => (
            <Link
              key={decision.id}
              to={`/decisions/${decision.id}`}
              className="block bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <h3 className="text-lg font-medium text-gray-900 mb-2">{decision.title}</h3>
              {decision.description?.trim() && (
                <p className="text-gray-600 text-sm mb-4">{decision.description}</p>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="capitalize text-gray-500">{decision.type}</span>
                <span className="capitalize text-gray-500">{decision.status}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}